import { useMemo, useState } from "react";

import { Colors } from "@blueprintjs/core";
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import xss from "xss";

import StarSVG from "@/../../hypetrain/static/img/star.svg";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { pluralize, unescape } from "@/components/helpers/string-utils";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { decodeHtmlEntities } from "@/components/pages_logged_in/roleplays/details/scenario/Notes";
import { Avatar } from "@/components/pieces/users/Avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { CARD_CLASSES } from "@/css/constants";
import { GetSessionFeedbackQuery, useGetSessionFeedbackQuery } from "@/graphql";
import { MainComponent } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";
import { NonNullableArrayItem } from "@/utils/types";

type Participant = NonNullableArrayItem<
  NonNullable<GetSessionFeedbackQuery["sessionFeedback"]>["participants"]
>;

function RatingStats({
  averageRating,
  totalRatings,
  distribution,
}: {
  averageRating: number;
  totalRatings: number;
  distribution: { score: number; count: number }[];
}) {
  const scoreLevel = [1, 2, 3, 4, 5];
  const _distribution = scoreLevel.map((score) => {
    const item = distribution?.find((item) => item.score === score);
    return {
      score,
      count: item?.count || 0,
    };
  });
  return (
    <Flex
      css={{
        border: "1px solid #E2E2E2",
        borderRadius: 10,
        padding: 24,
        marginBottom: 24,
        gap: 48,
      }}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        flexDirection={"column"}
        css={{ gap: "6px" }}
      >
        <Flex flexDirection="column" alignItems="center">
          <Flex alignItems="center" css={{ gap: "6px" }}>
            <EmbedSvg src={StarSVG} height={24} width={24} />
            <Box fontSize={36} fontWeight={600}>
              {averageRating?.toFixed(1) || 0}
            </Box>
          </Flex>
          <Box fontSize={14} color={Colors.GRAY1}>
            Average rating
          </Box>
        </Flex>
        <Flex flexDirection="column" alignItems="center">
          <Box fontSize={36} fontWeight={600}>
            {totalRatings}
          </Box>
          <Box fontSize={14} color={Colors.GRAY1}>
            Total ratings
          </Box>
        </Flex>
      </Flex>
      <Box css={{ borderRight: "1px solid #E2E2E2" }} />
      <Flex flexDirection="column" css={{ gap: "20px", flex: 1 }}>
        {_distribution.map((item) => (
          <Flex
            key={`stars-${item.score}`}
            alignItems="center"
            justifyContent="flex-start"
            css={{ gap: "16px" }}
          >
            <Box fontSize={14} fontWeight={500} width="50px">
              {item.score} {item.score === 1 ? "star" : "stars"}
            </Box>
            <Box
              css={{
                flex: 1,
                height: 12,
                backgroundColor: "#E2E2E2",
                borderRadius: 10,
                position: "relative",
              }}
            >
              <Box
                css={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${(item.count / totalRatings) * 100}%`,
                  backgroundColor: "#3788EE",
                  borderRadius: 100,
                }}
              />
            </Box>
            <Box fontSize={14} width="70px">
              {pluralize({
                amount: item.count,
                singular: "rating",
                plural: "ratings",
              })}
            </Box>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

interface SessionFeedbackProps {
  component: MainComponent;
}

export function SessionFeedback({ component }: SessionFeedbackProps) {
  const { data, loading } = useGetSessionFeedbackQuery({
    variables: {
      programComponentId: component.id,
    },
  });

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "customer",
      desc: false,
    },
  ]);

  const participants = useMemo(() => {
    return ensureNonNullArray(data?.sessionFeedback?.participants);
  }, [data?.sessionFeedback?.participants]);

  const sessionFeedback = data?.sessionFeedback;

  const columns: ColumnDef<Participant>[] = [
    {
      id: "customer",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Participant" />
      ),
      accessorKey: "customer",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.customer?.name?.toLowerCase() || "";
        const nameB = rowB.original.customer?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        return (
          <Flex alignItems="center">
            <Box mr={2}>
              <Tooltip content={row.original.customer?.email}>
                <Avatar size={20} user={row.original.customer} />
              </Tooltip>
            </Box>
            <Box style={{ fontSize: 16 }}>
              {row.original.customer?.name ||
                row.original.customer?.email ||
                ""}
            </Box>
          </Flex>
        );
      },
    },
    {
      id: "coach",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Coach" />
      ),
      accessorKey: "coach",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.coach?.name?.toLowerCase() || "";
        const nameB = rowB.original.coach?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        return (
          <Flex alignItems="center">
            <Box mr={2}>
              <Tooltip content={row.original.coach?.name}>
                <Avatar size={20} user={row.original.coach} />
              </Tooltip>
            </Box>
            <Box style={{ fontSize: 16 }}>{row.original.coach?.name || ""}</Box>
          </Flex>
        );
      },
    },
    {
      id: "score",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Rating" />
      ),
      accessorKey: "score",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const scoreA = rowA.original.score || 0;
        const scoreB = rowB.original.score || 0;
        return scoreA - scoreB;
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        return (
          <Flex alignItems="center" css={{ gap: "8px" }}>
            <EmbedSvg src={StarSVG} height={16} width={16} />
            <Box>{row.original.score}</Box>
          </Flex>
        );
      },
    },
    {
      id: "comment",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Feedback" />
      ),
      accessorKey: "comment",
      enableSorting: false,
      cell: ({ row }: { row: Row<Participant> }) => {
        return (
          <Box
            lineHeight="1.3em"
            style={{ maxWidth: 320, minWidth: 240, whiteSpace: "normal" }}
          >
            {unescape(xss(decodeHtmlEntities(row.original.comment || "")))}
          </Box>
        );
      },
    },
    {
      id: "meeting",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Session" />
      ),
      accessorKey: "meeting",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.original.meeting?.eventAt || "");
        const dateB = new Date(rowB.original.meeting?.eventAt || "");
        return dateA.getTime() - dateB.getTime();
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        return (
          <Flex flexDirection="column">
            <Box>{row.original.meeting?.product?.name}</Box>
            <Box fontSize={14} color={Colors.GRAY1}>
              {formatDate(row.original.meeting?.eventAt, "MMM d, yyyy")}
            </Box>
          </Flex>
        );
      },
    },
  ];

  const table = useReactTable({
    data: participants,
    columns,
    state: {
      sorting,
    },
    enableSorting: true,
    enableHiding: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => `${row?.customer?.id}-${row?.coach?.id}`,
  });

  const loadingSkeleton = (
    <div className="flex flex-col gap-4 h-full w-full">
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
    >
      <Box fontSize={18} fontWeight={600}>
        Session Feedback
      </Box>
      <Box mt={24}>
        <RatingStats
          averageRating={sessionFeedback?.averageRating || 0}
          totalRatings={sessionFeedback?.totalRating || 0}
          distribution={
            sessionFeedback?.ratingScoreCounts?.map((item) => ({
              score: item?.score || 0,
              count: item?.count || 0,
            })) || []
          }
        />
      </Box>

      <DataTable
        table={table}
        className="overflow-visible"
        pagination={false}
        emptyState={
          <div className="flex flex-col items-center justify-center py-8">
            {loading ? (
              loadingSkeleton
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-500">
                No feedback
              </div>
            )}
          </div>
        }
      />
    </Box>
  );
}
